import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { CalendarDays, Users, ArrowRight } from "lucide-react";
import { RevealLine, Overline } from "@/components/motion";
import { useContent } from "@/lib/content";

const HERO_IMG = "/images/restaurant.jpg";

const today = () => new Date().toISOString().slice(0, 10);
const plusDays = (d, n) => {
  const t = new Date(d);
  t.setDate(t.getDate() + n);
  return t.toISOString().slice(0, 10);
};

export default function Hero() {
  const navigate = useNavigate();
  const c = useContent();
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 900], [0, 220]);
  const [checkIn, setCheckIn] = useState(today());
  const [checkOut, setCheckOut] = useState(plusDays(today(), 2));
  const [guests, setGuests] = useState(2);

  const submit = (e) => {
    e.preventDefault();
    navigate(`/buchen?check_in=${checkIn}&check_out=${checkOut}&guests=${guests}`);
  };

  return (
    <section className="relative min-h-[96vh] flex flex-col justify-end overflow-hidden grain" data-testid="hero-section">
      <motion.div style={{ y }} className="absolute inset-0 scale-110">
        <img
          src={c.hero_image || HERO_IMG}
          alt="Restaurant im Hotel Wildpferd Dülmen mit Holztischen und Backsteinwand"
          className="w-full h-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-forest-dark/85 via-forest-dark/35 to-forest-dark/55" />

      <div className="relative z-10 max-w-7xl mx-auto w-full px-4 sm:px-8 lg:px-16 pb-10 pt-40">
        <Overline light>{c.hero_overline}</Overline>
        <h1 className="mt-5 font-serif font-semibold text-sand text-4xl sm:text-5xl lg:text-7xl leading-[1.05] tracking-tight text-balance max-w-4xl">
          <RevealLine delay={0.35}>{c.hero_title_1}</RevealLine>
          <RevealLine delay={0.5}>
            <em className="italic font-medium text-gold">{c.hero_title_2}</em>
          </RevealLine>
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.85, ease: [0.16, 1, 0.3, 1] }}
          className="mt-6 text-sand/80 text-base sm:text-lg max-w-xl leading-relaxed"
        >
          {c.hero_subtitle}
        </motion.p>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.05, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 bg-sand/95 backdrop-blur-md rounded-2xl p-4 sm:p-5 shadow-2xl shadow-forest-dark/40 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_1fr_auto] gap-3 max-w-4xl"
          data-testid="hero-booking-widget"
        >
          <label className="flex flex-col gap-1 px-3 py-2 rounded-xl bg-white/70 border border-forest/10">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/50 flex items-center gap-1.5">
              <CalendarDays size={12} /> Anreise
            </span>
            <input
              type="date"
              data-testid="hero-checkin-input"
              value={checkIn}
              min={today()}
              onChange={(e) => setCheckIn(e.target.value)}
              className="bg-transparent text-sm font-semibold text-ink outline-none"
            />
          </label>
          <label className="flex flex-col gap-1 px-3 py-2 rounded-xl bg-white/70 border border-forest/10">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/50 flex items-center gap-1.5">
              <CalendarDays size={12} /> Abreise
            </span>
            <input
              type="date"
              data-testid="hero-checkout-input"
              value={checkOut}
              min={plusDays(checkIn, 1)}
              onChange={(e) => setCheckOut(e.target.value)}
              className="bg-transparent text-sm font-semibold text-ink outline-none"
            />
          </label>
          <label className="flex flex-col gap-1 px-3 py-2 rounded-xl bg-white/70 border border-forest/10">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/50 flex items-center gap-1.5">
              <Users size={12} /> Gäste
            </span>
            <select
              data-testid="hero-guests-select"
              value={guests}
              onChange={(e) => setGuests(e.target.value)}
              className="bg-transparent text-sm font-semibold text-ink outline-none"
            >
              {[1, 2, 3, 4].map((n) => (
                <option key={n} value={n}>{n} {n === 1 ? "Gast" : "Gäste"}</option>
              ))}
            </select>
          </label>
          <button
            type="submit"
            data-testid="hero-search-button"
            className="group rounded-xl bg-forest text-sand px-7 py-4 text-sm font-semibold flex items-center justify-center gap-2 hover:bg-forest-dark transition-colors duration-300"
          >
            Verfügbarkeit prüfen
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </motion.form>
      </div>
    </section>
  );
}
